import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";
import {
  BLOG_CHAR_OPTIONS,
  BLOG_IMAGE_CREDITS,
  blogCreditCost,
  blogTotalCreditCost,
  SITE,
} from "../../shared/constants";

interface BlogFolder {
  id: string;
  scope_type: "office" | "service_cluster";
  service_cluster_id: string | null;
  service_key: string | null;
  service_name: string | null;
  page_id: string | null;
  keyword_id: string | null;
  name: string;
  target_keyword: string | null;
  main_page_title: string | null;
  main_page_path: string | null;
  purpose: string;
  status: string;
  counts: { total: number; published: number; draft: number };
}

interface BlogListItem {
  id: string;
  title: string;
  slug: string;
  status: "draft" | "published";
  credits_spent: number;
  scope_type: "office" | "service_cluster" | "keyword_folder" | "page";
  target_page_id: string | null;
  target_keyword_id: string | null;
  service_cluster_id: string | null;
  primary_support_page_id: string | null;
  target_keyword: string | null;
  article_type: string | null;
  search_intent: string | null;
  role_summary: string | null;
  internal_links: string;
  cta_label: string | null;
  cover_image_url: string | null;
  scheduled_at: string | null;
  updated_at: string;
  published_at: string | null;
  canonical_path?: string | null;
}

interface BlogPost extends BlogListItem {
  body: string;
}

const jstDate = (s: string) =>
  new Date(s).toLocaleString("ja-JP", { dateStyle: "medium", timeStyle: "short" });

const JST_TZ = "Asia/Tokyo";

type JstYmd = { y: number; m: number; d: number };

// ISO文字列を JST(Asia/Tokyo) 基準の年月日に変換する。カレンダーのセル割り当てはこの日付で行う。
function jstYmd(iso: string): JstYmd | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: JST_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const pick = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const y = pick("year");
  const m = pick("month");
  const d = pick("day");
  if (!y || !m || !d) return null;
  return { y, m, d };
}

// 今日(JST)の年月日。
function jstToday(): JstYmd {
  return jstYmd(new Date().toISOString()) ?? { y: 1970, m: 1, d: 1 };
}

const ymdKey = (ymd: JstYmd) => `${ymd.y}-${ymd.m}-${ymd.d}`;
const ymdOrder = (ymd: JstYmd) => ymd.y * 10000 + ymd.m * 100 + ymd.d;

const CALENDAR_WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

type CalendarCategory = "scheduled" | "published" | "draft";

// ステータスは色ではなく副次表現(塗り/枠線+チェック/破線)で示す。色分けはクラスター別。
const CALENDAR_STATUS_META: Record<CalendarCategory, { label: string; className: string; hint: string }> = {
  scheduled: { label: "予約", className: "status-scheduled", hint: "塗り" },
  published: { label: "公開", className: "status-published", hint: "枠線+✓" },
  draft: { label: "下書き", className: "status-draft", hint: "破線" },
};

// クラスター別の色パレット。落ち着いた彩度で、白背景で文字コントラストが取れる濃さに揃える。
// 並び順は隣り合う色相が離れるよう配置し、フォルダ順にインデックス割当すると同系色が隣接しにくい。
const CLUSTER_PALETTE = [
  "#2f6db0", // 青
  "#c07a1e", // 琥珀
  "#2f8f6b", // 緑
  "#a8497e", // 赤紫
  "#5b62b8", // 藍
  "#b85a3c", // テラコッタ
  "#2d8aa6", // 青緑
  "#7a8b2a", // オリーブ
  "#994f5a", // 臙脂
  "#556b8d", // 鉄紺
];
const CLUSTER_NEUTRAL_COLOR = "#8a95a6"; // クラスター未所属(事務所全体)の中立色
const OFFICE_CLUSTER_KEY = "__office__";
const OFFICE_CLUSTER_LABEL = "事務所全体";

interface ClusterAssignment {
  key: string;
  name: string;
  color: string;
}

interface CalendarEventBase {
  post: BlogListItem;
  iso: string;
  date: JstYmd;
  category: CalendarCategory;
}

interface CalendarEvent extends CalendarEventBase {
  cluster: ClusterAssignment;
}

// クラスターごとの色/名前の決定的割当と、投稿→クラスター解決を組み立てる。
// service_cluster フォルダの並び(created_at 昇順)に沿ってパレットをインデックス割当するため、
// 同じデータなら常に同じ色になり、隣接クラスターの色も離れる。
function buildClusterResolver(folders: BlogFolder[]) {
  const clusterFolders = folders.filter((f) => f.scope_type === "service_cluster");
  const colorById = new Map<string, string>();
  clusterFolders.forEach((folder, index) => {
    colorById.set(folder.id, CLUSTER_PALETTE[index % CLUSTER_PALETTE.length] ?? CLUSTER_NEUTRAL_COLOR);
  });
  const resolve = (post: BlogListItem): ClusterAssignment => {
    const match = clusterFolders.find(
      (folder) =>
        (folder.service_cluster_id && post.service_cluster_id === folder.service_cluster_id)
        || (folder.page_id && (post.primary_support_page_id === folder.page_id || post.target_page_id === folder.page_id))
        || (folder.keyword_id && post.target_keyword_id === folder.keyword_id),
    );
    if (match) {
      return { key: match.id, name: match.name, color: colorById.get(match.id) ?? CLUSTER_NEUTRAL_COLOR };
    }
    return { key: OFFICE_CLUSTER_KEY, name: OFFICE_CLUSTER_LABEL, color: CLUSTER_NEUTRAL_COLOR };
  };
  return { clusterFolders, resolve };
}

// 全投稿をカレンダー上のイベントに変換する。
// 公開済み → published_at(なければ updated_at)、予約(scheduled_at あり) → scheduled_at、
// それ以外の下書き → updated_at に配置する。JST基準で日付を決める。
function toCalendarEvent(post: BlogListItem): CalendarEventBase | null {
  let iso: string | null;
  let category: CalendarCategory;
  if (post.status === "published") {
    iso = post.published_at ?? post.updated_at ?? null;
    category = "published";
  } else if (post.scheduled_at) {
    iso = post.scheduled_at;
    category = "scheduled";
  } else {
    iso = post.updated_at ?? null;
    category = "draft";
  }
  if (!iso) return null;
  const date = jstYmd(iso);
  if (!date) return null;
  return { post, iso, date, category };
}

const statusLabel = (status: string) => {
  if (status === "published") return "公開中";
  if (status === "active") return "運用中";
  if (status === "paused") return "保留中";
  if (status === "approved") return "作成待ち";
  if (status === "draft") return "下書き";
  return "準備中";
};

const parseInternalLinks = (value: string): Array<{ label: string; url: string; kind?: string }> => {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const markdownHeadings = (body: string) =>
  body
    .split(/\r?\n/)
    .map((line) => line.match(/^##\s+(.+)$/)?.[1]?.trim())
    .filter((line): line is string => Boolean(line))
    .slice(0, 8);

function appBlogImageUrl(caseId: string | null | undefined, imageUrl: string | null | undefined): string | null {
  const raw = String(imageUrl ?? "").trim();
  if (!caseId || !raw) return raw || null;
  let pathname = raw;
  try {
    pathname = new URL(raw, window.location.origin).pathname;
  } catch {
    pathname = raw;
  }
  const prefix = "/blog/assets/";
  if (!pathname.startsWith(prefix)) return raw;
  const parts = pathname.slice(prefix.length).split("/").filter(Boolean);
  if (parts.length < 2) return raw;
  const [postId, ...fileParts] = parts;
  const file = fileParts.join("/");
  if (!postId || !file) return raw;
  return `/api/cases/${caseId}/asset?key=${encodeURIComponent(`cases/${caseId}/blog/${postId}/${file}`)}`;
}

export function BlogStudioPage() {
  const { caseRow, loading } = useCurrentCase();
  const [blogCase, setBlogCase] = useState<typeof caseRow>(null);
  const [folders, setFolders] = useState<BlogFolder[]>([]);
  const [posts, setPosts] = useState<BlogListItem[]>([]);
  const [siteSlug, setSiteSlug] = useState<string | null>(null);
  const [balance, setBalance] = useState(0);
  const [selectedFolderId, setSelectedFolderId] = useState("office");
  const [view, setView] = useState<"overview" | "calendar" | "folder" | "editor">("overview");
  const [editorReturn, setEditorReturn] = useState<"overview" | "calendar" | "folder">("folder");
  const [theme, setTheme] = useState("");
  const [targetChars, setTargetChars] = useState<number>(BLOG_CHAR_OPTIONS[0]);
  const [bulkCount, setBulkCount] = useState(5);
  const [folderTab, setFolderTab] = useState<"published" | "draft" | "proposal">("draft");
  const [themesByFolder, setThemesByFolder] = useState<Record<string, string[]>>({});
  const [suggesting, setSuggesting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fetching, setFetching] = useState(true);

  const reload = async (caseId: string) => {
    const [list, bal] = await Promise.all([
      api.get<{ posts: BlogListItem[]; folders: BlogFolder[]; slug: string | null }>(
        `/api/cases/${caseId}/blog`,
      ),
      api.get<{ balance: number }>("/api/cases/credits/balance"),
    ]);
    setPosts(list.posts);
    setFolders(list.folders);
    setSiteSlug(list.slug);
    setBalance(bal.balance);
    if (!list.folders.some((folder) => folder.id === selectedFolderId)) {
      setSelectedFolderId(list.folders[0]?.id ?? "office");
    }
  };

  useEffect(() => {
    if (!caseRow) return;
    let mounted = true;
    const resolveCase = async () => {
      if (caseRow.status === "published" || caseRow.status === "improving") return caseRow;
      const res = await api.get<{ cases: Array<typeof caseRow> }>("/api/cases");
      return res.cases.find((item) => item && (item.status === "published" || item.status === "improving")) ?? null;
    };
    resolveCase()
      .then((target) => {
        if (!mounted || !target) return;
        setBlogCase(target);
        return reload(target.id);
      })
      .catch((err) =>
        setError(err instanceof ApiRequestError ? err.message : "読み込みに失敗しました"),
      )
      .finally(() => setFetching(false));
    return () => {
      mounted = false;
    };
  }, [caseRow]);

  const selectedFolder = useMemo(
    () => folders.find((folder) => folder.id === selectedFolderId) ?? folders[0],
    [folders, selectedFolderId],
  );

  const folderPosts = useMemo(() => {
    if (!selectedFolder) return [];
    if (selectedFolder.id === "office") {
      return posts.filter((p) => p.scope_type === "office" || (!p.service_cluster_id && !p.target_page_id && !p.target_keyword_id));
    }
    return posts.filter(
      (p) =>
        p.service_cluster_id === selectedFolder.service_cluster_id
        || p.primary_support_page_id === selectedFolder.page_id
        || p.target_page_id === selectedFolder.page_id
        || p.target_keyword_id === selectedFolder.keyword_id,
    );
  }, [posts, selectedFolder]);

  const publishedPosts = folderPosts.filter((p) => p.status === "published");
  const draftPosts = folderPosts.filter((p) => p.status === "draft");
  const selectedThemes = selectedFolder ? themesByFolder[selectedFolder.id] ?? [] : [];
  const cost = blogTotalCreditCost(targetChars);
  const bulkCost = cost * bulkCount;
  const enough = balance >= cost;
  const enoughBulk = balance >= bulkCost;

  const folderPayload = (folder: BlogFolder) => ({
    scope_type: folder.scope_type,
    service_cluster_id: folder.service_cluster_id,
    primary_support_page_id: folder.page_id,
    target_page_id: folder.page_id,
    target_keyword_id: folder.keyword_id,
    target_keyword: folder.target_keyword,
    article_type: folder.scope_type === "office" ? "事務所全体ブログ" : "専門ページ関連記事",
    role_summary: folder.purpose,
  });

  const selectFolder = (folder: BlogFolder) => {
    setSelectedFolderId(folder.id);
    setView("folder");
    setFolderTab("draft");
    setTheme("");
    setNotice(null);
    setError(null);
  };

  const suggestThemes = async (folder: BlogFolder) => {
    if (!blogCase) return;
    setSelectedFolderId(folder.id);
    setError(null);
    setNotice(null);
    setSuggesting(true);
    try {
      const res = await api.post<{ themes: string[] }>(
        `/api/cases/${blogCase.id}/blog/suggest-themes`,
        folderPayload(folder),
      );
      setThemesByFolder((prev) => ({ ...prev, [folder.id]: res.themes }));
      setView("folder");
      if (res.themes.length === 0) {
        setNotice("記事案が見つかりませんでした。テーマを直接入力してください。");
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "記事案の提案に失敗しました");
    } finally {
      setSuggesting(false);
    }
  };

  const generate = async () => {
    if (!blogCase || !selectedFolder) return;
    if (!theme.trim()) {
      setError("記事のテーマを入力してください");
      return;
    }
    setError(null);
    setNotice(null);
    setGenerating(true);
    try {
      const res = await api.post<{ post: BlogPost; balance: number }>(
        `/api/cases/${blogCase.id}/blog/generate`,
        { theme: theme.trim(), target_chars: targetChars, ...folderPayload(selectedFolder) },
      );
      setBalance(res.balance);
      setEditing(res.post);
      setTheme("");
      setNotice("記事を生成しました。内容を確認して、必要に応じて編集できます。");
      await reload(blogCase.id);
      setView("editor");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "記事生成に失敗しました");
    } finally {
      setGenerating(false);
    }
  };

  const generateBulk = async () => {
    if (!blogCase || !selectedFolder) return;
    setError(null);
    setNotice(null);
    setBulkGenerating(true);
    try {
      const res = await api.post<{ posts: BlogPost[]; balance: number; cost: number }>(
        `/api/cases/${blogCase.id}/blog/bulk-generate`,
        { count: bulkCount, target_chars: targetChars, ...folderPayload(selectedFolder) },
      );
      setBalance(res.balance);
      setNotice(`${res.posts.length}本の記事を下書きに追加し、1日1本の予約投稿を設定しました。`);
      await reload(blogCase.id);
      setView("calendar");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "記事のまとめ生成に失敗しました");
    } finally {
      setBulkGenerating(false);
    }
  };

  const openEditor = async (postId: string) => {
    if (!blogCase) return;
    setError(null);
    setNotice(null);
    setEditorReturn(view === "calendar" ? "calendar" : "folder");
    try {
      const res = await api.get<{ post: BlogPost }>(`/api/cases/${blogCase.id}/blog/${postId}`);
      setEditing(res.post);
      setView("editor");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "記事の取得に失敗しました");
    }
  };

  const save = async (status: "draft" | "published") => {
    if (!blogCase || !editing) return;
    setError(null);
    setSaving(true);
    try {
      const res = await api.put<{ post: BlogPost }>(
        `/api/cases/${blogCase.id}/blog/${editing.id}`,
        { title: editing.title, body: editing.body, status },
      );
      setEditing(res.post);
      setNotice(status === "published" ? "記事を公開しました。" : "下書きを保存しました。");
      await reload(blogCase.id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (postId: string) => {
    if (!blogCase) return;
    setError(null);
    try {
      await api.delete(`/api/cases/${blogCase.id}/blog/${postId}`);
      if (editing?.id === postId) setEditing(null);
      setNotice("記事を削除しました。");
      await reload(blogCase.id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "削除に失敗しました");
    }
  };

  if (loading || fetching) return <div className="page-center">読み込み中...</div>;
  if (!blogCase) return <div className="page-center">公開済みサイトが見つかりません。</div>;

  const canonicalPublicUrl = (post: Pick<BlogListItem, "slug" | "canonical_path">) => {
    if (!siteSlug) return null;
    return `https://${siteSlug}.${SITE.domainSuffix}${post.canonical_path || `/blog/${post.slug}/`}`;
  };

  const overviewStats = {
    folders: folders.filter((folder) => folder.scope_type === "service_cluster").length,
    published: posts.filter((p) => p.status === "published").length,
    drafts: posts.filter((p) => p.status === "draft").length,
    proposals: Object.values(themesByFolder).reduce((sum, themes) => sum + themes.length, 0),
  };

  return (
    <section className="lp-page blog-os-page">
      <div className="lp-page-head">
        <div>
          <span className="lp-section-kicker">AIブログ</span>
          <h1>業務クラスターを育てるブログ管理</h1>
          <p>
            ブログ記事を、事務所全体または業務クラスターに紐づけて管理します。
          </p>
        </div>
        {(view === "folder" || view === "editor") && (
          <button className="btn btn-ghost" onClick={() => setView("overview")}>
            AIブログトップへ
          </button>
        )}
      </div>

      {(view === "overview" || view === "calendar") && (
        <div className="blog-toplevel-tabs">
          <button
            type="button"
            className={view === "overview" ? "active" : ""}
            onClick={() => setView("overview")}
          >
            フォルダ / 記事
          </button>
          <button
            type="button"
            className={view === "calendar" ? "active" : ""}
            onClick={() => {
              setView("calendar");
              setError(null);
              setNotice(null);
            }}
          >
            投稿カレンダー
          </button>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      {view === "calendar" && (
        <BlogCalendar posts={posts} folders={folders} openEditor={openEditor} />
      )}

      {view === "overview" && (
        <>
          <div className="blog-stats">
            <div className="lp-card">
              <span>業務クラスター</span>
              <strong>{overviewStats.folders}</strong>
            </div>
            <div className="lp-card">
              <span>公開中の記事</span>
              <strong>{overviewStats.published}</strong>
            </div>
            <div className="lp-card">
              <span>下書き</span>
              <strong>{overviewStats.drafts}</strong>
            </div>
            <div className="lp-card">
              <span>AI提案中</span>
              <strong>{overviewStats.proposals}</strong>
            </div>
          </div>

          <div className="blog-overview-grid">
            <div className="blog-folder-grid">
              {folders.map((folder) => (
                <article key={folder.id} className="blog-folder-card lp-card">
                  <div className="blog-folder-card-head">
                    <span className="blog-folder-type">
                      {folder.scope_type === "office" ? "トップページ配下" : "業務クラスター"}
                    </span>
                    <span className="pill pill-gray">{statusLabel(folder.status)}</span>
                  </div>
                  <h2>{folder.name}</h2>
                  <dl>
                    <div>
                      <dt>狙う検索</dt>
                      <dd>{folder.target_keyword || folder.service_name || "事務所全体の信頼形成"}</dd>
                    </div>
                    <div>
                      <dt>関連記事</dt>
                      <dd>
                        {folder.counts.total}本（公開 {folder.counts.published} / 下書き{" "}
                        {folder.counts.draft}）
                      </dd>
                    </div>
                  </dl>
                  <p>{folder.purpose}</p>
                  <div className="blog-card-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => selectFolder(folder)}>
                      記事を見る
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => suggestThemes(folder)}
                      disabled={suggesting}
                    >
                      {suggesting && selectedFolderId === folder.id ? "提案中..." : "AIに記事案を出してもらう"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
            <aside className="blog-next lp-card">
              <h2>次にやること</h2>
              <ol>
                <li>育てたい専門ページを選びます。</li>
                <li>AIに記事案を出してもらいます。</li>
                <li>記事を確認して公開します。</li>
              </ol>
              <p className="muted small">
                専門ページがまだ少ない場合は、先にページ設計で狙うキーワードごとのページを作成してください。
              </p>
              <Link to="/page-plan" className="btn btn-ghost btn-sm">
                ページ設計を見る
              </Link>
            </aside>
          </div>
        </>
      )}

      {view === "folder" && selectedFolder && (
        <div className="blog-folder-view">
          <div className="blog-breadcrumb">
            <button onClick={() => setView("overview")}>AIブログ</button>
            <span>/</span>
            <strong>{selectedFolder.name}</strong>
          </div>
          <div className="blog-folder-summary lp-card">
            <div>
              <span className="lp-section-kicker">関連記事管理</span>
              <h2>{selectedFolder.name}</h2>
              <p>{selectedFolder.purpose}</p>
              <div className="blog-summary-grid">
                <div>
                  <span>メインページ</span>
                  <strong>{selectedFolder.main_page_title || "トップページ"}</strong>
                </div>
                <div>
                  <span>狙う検索</span>
                  <strong>{selectedFolder.target_keyword || "事務所全体の信頼形成"}</strong>
                </div>
              </div>
            </div>
            <div className="blog-summary-actions">
              <button className="btn btn-primary" onClick={() => suggestThemes(selectedFolder)} disabled={suggesting}>
                {suggesting ? "記事案を作成中..." : "AIに記事案を出してもらう"}
              </button>
              {selectedFolder.main_page_path && (
                <Link to="/page-plan" className="btn btn-ghost">
                  メインページを見る
                </Link>
              )}
            </div>
          </div>

          <div className="blog-folder-grid-2">
            <main className="blog-article-lanes">
              <div className="blog-tabs lp-card">
                <button className={folderTab === "draft" ? "active" : ""} onClick={() => setFolderTab("draft")}>
                  下書き {draftPosts.length}
                </button>
                <button className={folderTab === "published" ? "active" : ""} onClick={() => setFolderTab("published")}>
                  公開中 {publishedPosts.length}
                </button>
                <button className={folderTab === "proposal" ? "active" : ""} onClick={() => setFolderTab("proposal")}>
                  AI提案 {selectedThemes.length}
                </button>
              </div>

              {folderTab === "published" && (
                <BlogLane posts={publishedPosts} openEditor={openEditor} publicUrl={canonicalPublicUrl} remove={remove} caseId={blogCase?.id ?? null} />
              )}
              {folderTab === "draft" && (
                <BlogLane posts={draftPosts} openEditor={openEditor} publicUrl={canonicalPublicUrl} remove={remove} caseId={blogCase?.id ?? null} />
              )}
              {folderTab === "proposal" && (
                <section className="blog-lane lp-card">
                  <h3>AI提案</h3>
                  {selectedThemes.length === 0 ? (
                    <p className="muted">まだ提案はありません。上のボタンから記事案を作成できます。</p>
                  ) : (
                    <div className="blog-theme-list">
                      {selectedThemes.map((item) => (
                        <button key={item} className="blog-theme-row" onClick={() => setTheme(item)}>
                          <strong>{item}</strong>
                          <span>このテーマで作成する</span>
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              )}
            </main>

            <aside className="blog-generate-panel lp-card">
              <h3>記事を作成する</h3>
              <label className="field">
                <span className="field-label">テーマ</span>
                <input
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  placeholder="例）建設業許可の要件で最初に確認すること"
                />
              </label>
              <label className="field">
                <span className="field-label">目安の文字数</span>
                <select value={targetChars} onChange={(e) => setTargetChars(Number(e.target.value))}>
                  {BLOG_CHAR_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      約{n.toLocaleString("ja-JP")}字（{blogTotalCreditCost(n)}cr）
                    </option>
                  ))}
                </select>
              </label>
              <p className="muted small">
                消費見積り: <strong>{cost}cr</strong>（記事 {blogCreditCost(targetChars)}cr + 画像3枚{" "}
                {BLOG_IMAGE_CREDITS}cr）。残高 {balance.toLocaleString("ja-JP")}cr。
              </p>
              <button className="btn btn-primary" onClick={generate} disabled={generating || !enough}>
                {generating ? "生成中..." : `${cost}crで記事を生成`}
              </button>
              {!enough && (
                <p className="muted small">
                  クレジットが不足しています。<Link to="/dashboard">ダッシュボード</Link>からチャージしてください。
                </p>
              )}
              <div className="blog-bulk-panel">
                <h3>記事をまとめて生成する</h3>
                <label className="field">
                  <span className="field-label">生成する本数</span>
                  <select value={bulkCount} onChange={(e) => setBulkCount(Number(e.target.value))}>
                    {[5, 6, 7, 8, 9, 10].map((n) => (
                      <option key={n} value={n}>{n}本</option>
                    ))}
                  </select>
                </label>
                <p className="muted small">
                  {bulkCount}本を下書きにし、明日から1日1本ずつ投稿予定に入れます。消費見積り: <strong>{bulkCost}cr</strong>
                </p>
                <button className="btn btn-ghost btn-block" onClick={generateBulk} disabled={bulkGenerating || !enoughBulk}>
                  {bulkGenerating ? "まとめて生成中..." : `${bulkCount}本をまとめて生成`}
                </button>
                {!enoughBulk && <p className="muted small">まとめ生成に必要なクレジットが不足しています。</p>}
              </div>
            </aside>
          </div>
        </div>
      )}

      {view === "editor" && editing && (
        <BlogEditor
          post={editing}
          setPost={(post) => setEditing(post)}
          save={save}
          saving={saving}
          publicUrl={canonicalPublicUrl}
          caseId={blogCase?.id ?? null}
          close={() => {
            setEditing(null);
            setView(editorReturn);
          }}
        />
      )}
    </section>
  );
}

function BlogLane({
  posts,
  openEditor,
  publicUrl,
  remove,
  caseId,
}: {
  posts: BlogListItem[];
  openEditor: (postId: string) => void;
  publicUrl: (post: BlogListItem) => string | null;
  remove: (postId: string) => void;
  caseId: string | null;
}) {
  return (
    <section className="blog-lane lp-card">
      {posts.length === 0 ? (
        <p className="muted">該当する記事はまだありません。</p>
      ) : (
        <div className="blog-post-card-list">
          {posts.map((post) => {
            const url = publicUrl(post);
            return (
              <article key={post.id} className="blog-post-card">
                <div className="blog-post-cover">
                  {post.cover_image_url ? (
                    <img
                      src={appBlogImageUrl(caseId, post.cover_image_url) ?? post.cover_image_url}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.parentElement?.classList.add("is-missing");
                      }}
                    />
                  ) : <span>NO IMAGE</span>}
                  {post.cover_image_url && <span className="blog-post-cover-fallback">画像を確認中</span>}
                </div>
                <div className="blog-post-main">
                  <strong>{post.title}</strong>
                  <span>狙う検索: {post.target_keyword || "事務所全体"}</span>
                  <span>更新: {jstDate(post.updated_at)}</span>
                  {post.scheduled_at && <span>投稿予定: {jstDate(post.scheduled_at)}</span>}
                </div>
                <div className="blog-post-actions">
                  {url && post.status === "published" && (
                    <a className="btn btn-ghost btn-sm" href={url} target="_blank" rel="noreferrer">
                      公開URL
                    </a>
                  )}
                  <button className="btn btn-primary btn-sm" onClick={() => openEditor(post.id)}>
                    確認する
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => remove(post.id)}>
                    削除
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function BlogCalendar({
  posts,
  folders,
  openEditor,
}: {
  posts: BlogListItem[];
  folders: BlogFolder[];
  openEditor: (postId: string) => void;
}) {
  const today = useMemo(() => jstToday(), []);
  const [cursor, setCursor] = useState<{ y: number; m: number }>({ y: today.y, m: today.m });
  // クラスター凡例をクリックすると、そのクラスターだけに絞り込む(null=全表示)。
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);

  const resolver = useMemo(() => buildClusterResolver(folders), [folders]);

  const events = useMemo(() => {
    const list: CalendarEvent[] = [];
    for (const post of posts) {
      const base = toCalendarEvent(post);
      if (!base) continue;
      list.push({ ...base, cluster: resolver.resolve(post) });
    }
    return list;
  }, [posts, resolver]);

  // 実際に記事があるクラスターだけを、フォルダ順(事務所全体は末尾)で凡例に出す。
  const legendItems = useMemo(() => {
    const seen = new Map<string, ClusterAssignment>();
    for (const event of events) {
      if (!seen.has(event.cluster.key)) seen.set(event.cluster.key, event.cluster);
    }
    const ordered: ClusterAssignment[] = [];
    for (const folder of resolver.clusterFolders) {
      const item = seen.get(folder.id);
      if (item) ordered.push(item);
    }
    const office = seen.get(OFFICE_CLUSTER_KEY);
    if (office) ordered.push(office);
    return ordered;
  }, [events, resolver]);

  // 凡例から消えたクラスターを選択したままにしない。
  useEffect(() => {
    if (selectedCluster && !legendItems.some((item) => item.key === selectedCluster)) {
      setSelectedCluster(null);
    }
  }, [legendItems, selectedCluster]);

  const visibleEvents = useMemo(
    () => (selectedCluster ? events.filter((event) => event.cluster.key === selectedCluster) : events),
    [events, selectedCluster],
  );

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of visibleEvents) {
      const key = ymdKey(event.date);
      const bucket = map.get(key);
      if (bucket) bucket.push(event);
      else map.set(key, [event]);
    }
    return map;
  }, [visibleEvents]);

  // 日曜始まりの月グリッド。曜日はカレンダー日付なので UTC で算出（タイムゾーン非依存）。
  const weeks = useMemo(() => {
    const firstWeekday = new Date(Date.UTC(cursor.y, cursor.m - 1, 1)).getUTCDay();
    const daysInMonth = new Date(Date.UTC(cursor.y, cursor.m, 0)).getUTCDate();
    const cells: Array<{ day: number; key: string } | null> = [];
    for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push({ day: d, key: ymdKey({ y: cursor.y, m: cursor.m, d }) });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const rows: Array<Array<{ day: number; key: string } | null>> = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [cursor]);

  const upcoming = useMemo(() => {
    const todayOrder = ymdOrder(today);
    return visibleEvents
      .filter((event) => event.category === "scheduled" && ymdOrder(event.date) >= todayOrder)
      .sort((a, b) => a.iso.localeCompare(b.iso));
  }, [visibleEvents, today]);

  const shiftMonth = (delta: number) => {
    setCursor((prev) => {
      const base = new Date(Date.UTC(prev.y, prev.m - 1 + delta, 1));
      return { y: base.getUTCFullYear(), m: base.getUTCMonth() + 1 };
    });
  };
  const isCurrentMonth = cursor.y === today.y && cursor.m === today.m;
  const toggleCluster = (key: string) =>
    setSelectedCluster((prev) => (prev === key ? null : key));

  return (
    <section className="blog-lane lp-card blog-calendar">
      <div className="blog-calendar-head">
        <div>
          <h3>投稿カレンダー</h3>
          <p className="muted small">
            全キーワード横断の投稿スケジュールです。色は業務クラスター、塗り/枠線/破線はステータスを表します。
          </p>
        </div>
        <div className="blog-calendar-nav">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => shiftMonth(-1)} aria-label="前の月">
            ‹
          </button>
          <strong>{cursor.y}年{cursor.m}月</strong>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => shiftMonth(1)} aria-label="次の月">
            ›
          </button>
          {!isCurrentMonth && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCursor({ y: today.y, m: today.m })}>
              今日
            </button>
          )}
        </div>
      </div>

      <div className="blog-calendar-legend" role="group" aria-label="クラスターで絞り込み">
        {legendItems.length === 0 ? (
          <span className="muted small">表示できる記事がまだありません。</span>
        ) : (
          <>
            <button
              type="button"
              className={`blog-calendar-legend-item ${selectedCluster === null ? "is-active" : ""}`}
              onClick={() => setSelectedCluster(null)}
            >
              <span className="blog-calendar-legend-swatch is-all" aria-hidden="true" />
              すべて
            </button>
            {legendItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`blog-calendar-legend-item ${selectedCluster === item.key ? "is-active" : ""}`}
                style={{ ["--cluster-color" as string]: item.color }}
                onClick={() => toggleCluster(item.key)}
                aria-pressed={selectedCluster === item.key}
              >
                <span className="blog-calendar-legend-swatch" aria-hidden="true" />
                {item.name}
              </button>
            ))}
          </>
        )}
      </div>

      <div className="blog-calendar-status-legend">
        {(Object.keys(CALENDAR_STATUS_META) as CalendarCategory[]).map((category) => (
          <span key={category} className={`blog-calendar-status-item ${CALENDAR_STATUS_META[category].className}`}>
            <span className="blog-calendar-status-chip" aria-hidden="true">
              {category === "published" ? "✓" : ""}
            </span>
            {CALENDAR_STATUS_META[category].label}
          </span>
        ))}
      </div>

      <div className="blog-calendar-grid" role="grid" aria-label={`${cursor.y}年${cursor.m}月の投稿カレンダー`}>
        <div className="blog-calendar-weekhead" role="row">
          {CALENDAR_WEEKDAYS.map((label, index) => (
            <span
              key={label}
              role="columnheader"
              className={`blog-calendar-weekday ${index === 0 ? "is-sun" : ""} ${index === 6 ? "is-sat" : ""}`}
            >
              {label}
            </span>
          ))}
        </div>
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="blog-calendar-week" role="row">
            {week.map((cell, cellIndex) => {
              if (!cell) {
                return <div key={cellIndex} className="blog-calendar-cell is-empty" role="gridcell" aria-hidden="true" />;
              }
              const dayEvents = eventsByDay.get(cell.key) ?? [];
              const isToday = isCurrentMonth && cell.day === today.d;
              return (
                <div
                  key={cellIndex}
                  className={`blog-calendar-cell ${isToday ? "is-today" : ""} ${cellIndex === 0 ? "is-sun" : ""} ${cellIndex === 6 ? "is-sat" : ""}`}
                  role="gridcell"
                >
                  <span className="blog-calendar-daynum">{cell.day}</span>
                  <div className="blog-calendar-events">
                    {dayEvents.map((event) => (
                      <button
                        key={event.post.id}
                        type="button"
                        className={`blog-calendar-event ${CALENDAR_STATUS_META[event.category].className}`}
                        style={{ ["--cluster-color" as string]: event.cluster.color }}
                        onClick={() => openEditor(event.post.id)}
                        title={`${event.cluster.name}｜${CALENDAR_STATUS_META[event.category].label}｜${event.post.title}`}
                      >
                        <span className="blog-calendar-event-kw">
                          <span className="blog-calendar-event-dot" aria-hidden="true" />
                          {event.category === "published" && (
                            <span className="blog-calendar-event-check" aria-hidden="true">✓</span>
                          )}
                          {event.cluster.name}
                        </span>
                        <span className="blog-calendar-event-title">{event.post.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="blog-calendar-upcoming">
        <h3>今後の予定</h3>
        {upcoming.length === 0 ? (
          <p className="muted">予約投稿はまだありません。記事をまとめて生成すると自動で予定が入ります。</p>
        ) : (
          <div className="blog-calendar-list">
            {upcoming.map((event) => (
              <button
                key={event.post.id}
                type="button"
                className="blog-calendar-row"
                onClick={() => openEditor(event.post.id)}
              >
                <time>{jstDate(event.iso)}</time>
                <strong>{event.post.title}</strong>
                <span className="blog-calendar-row-cluster">
                  <span
                    className="blog-calendar-event-dot"
                    style={{ ["--cluster-color" as string]: event.cluster.color }}
                    aria-hidden="true"
                  />
                  {event.cluster.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function BlogEditor({
  post,
  setPost,
  save,
  saving,
  publicUrl,
  caseId,
  close,
}: {
  post: BlogPost;
  setPost: (post: BlogPost) => void;
  save: (status: "draft" | "published") => void;
  saving: boolean;
  publicUrl: (post: BlogPost) => string | null;
  caseId: string | null;
  close: () => void;
}) {
  const headings = markdownHeadings(post.body);
  const links = parseInternalLinks(post.internal_links);
  const [editingMarkdown, setEditingMarkdown] = useState(false);
  useEffect(() => setEditingMarkdown(false), [post.id]);
  return (
    <div className="blog-editor">
      <div className="blog-breadcrumb">
        <button onClick={close}>関連記事管理</button>
        <span>/</span>
        <strong>AI記事の確認</strong>
      </div>
      <div className="blog-editor-head lp-card">
        <div>
          <span className="lp-section-kicker">AI記事の確認</span>
          <input
            className="blog-title-input"
            value={post.title}
            onChange={(e) => setPost({ ...post, title: e.target.value })}
          />
          <div className="blog-editor-chips">
            <span>狙う検索: {post.target_keyword || "事務所全体"}</span>
            <span>{post.article_type || "関連記事"}</span>
            <span>{post.status === "published" ? "公開中" : "下書き"}</span>
          </div>
        </div>
        {post.status === "published" && publicUrl(post) && (
          <a className="btn btn-ghost" href={publicUrl(post)!} target="_blank" rel="noreferrer">
            公開ページを開く
          </a>
        )}
      </div>
      <div className="blog-editor-grid">
        <aside className="blog-outline lp-card">
          <h3>記事構成</h3>
          {headings.length === 0 ? (
            <p className="muted">見出しがまだありません。</p>
          ) : (
            headings.map((heading) => <span key={heading}>{heading}</span>)
          )}
        </aside>
        <main className="blog-body-editor lp-card">
          <div className="blog-body-head">
            <span className="field-label">本文</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditingMarkdown((v) => !v)}>
              {editingMarkdown ? "プレビュー" : "編集"}
            </button>
          </div>
          {editingMarkdown ? (
            <textarea
              value={post.body}
              onChange={(e) => setPost({ ...post, body: e.target.value })}
              rows={24}
              spellCheck={false}
            />
          ) : (
            <MarkdownPreview body={post.body} caseId={caseId} />
          )}
        </main>
        <aside className="blog-editor-side lp-card">
          <h3>この記事の役割</h3>
          <p>{post.role_summary || "専門ページへの相談導線を補強する記事です。"}</p>
          <h3>自動で入るリンク</h3>
          {links.length === 0 ? (
            <p className="muted">この記事には専門ページリンクが設定されていません。</p>
          ) : (
            links.map((link) => (
              <span key={`${link.label}-${link.url}`} className="blog-link-chip">
                {link.label}
              </span>
            ))
          )}
          <h3>公開前チェック</h3>
          <ul>
            <li>料金や実績に未確認の断定がない</li>
            <li>問い合わせへの案内が入っている</li>
            <li>専門ページへの内部リンクが自然に入っている</li>
          </ul>
        </aside>
      </div>
      <div className="blog-editor-actions">
        <button className="btn btn-ghost" onClick={() => save("draft")} disabled={saving}>
          下書き保存
        </button>
        {post.status === "published" ? (
          <button className="btn btn-ghost" onClick={() => save("draft")} disabled={saving}>
            非公開にする
          </button>
        ) : (
          <button className="btn btn-primary" onClick={() => save("published")} disabled={saving}>
            公開する
          </button>
        )}
        <button className="btn btn-ghost" onClick={close}>
          閉じる
        </button>
      </div>
    </div>
  );
}

function MarkdownPreview({ body, caseId }: { body: string; caseId: string | null }) {
  const blocks = body.split(/\n{2,}/).filter(Boolean);
  return (
    <div className="blog-markdown-preview">
      {blocks.map((block, index) => {
        const image = block.match(/!\[([^\]]*)]\(([^)]+)\)/);
        if (image) {
          return (
            <img
              key={`${index}-${image[2]}`}
              src={appBlogImageUrl(caseId, image[2]) ?? image[2]}
              alt={image[1]}
              onError={(e) => {
                e.currentTarget.replaceWith(document.createTextNode("画像を読み込めませんでした"));
              }}
            />
          );
        }
        if (block.startsWith("### ")) return <h3 key={index}>{block.replace(/^###\s+/, "")}</h3>;
        if (block.startsWith("## ")) return <h2 key={index}>{block.replace(/^##\s+/, "")}</h2>;
        if (block.startsWith("# ")) return <h1 key={index}>{block.replace(/^#\s+/, "")}</h1>;
        if (block.startsWith("- ")) {
          return (
            <ul key={index}>
              {block.split(/\r?\n/).map((line) => <li key={line}>{line.replace(/^-\s+/, "")}</li>)}
            </ul>
          );
        }
        return <p key={index}>{block.replace(/\n/g, " ")}</p>;
      })}
    </div>
  );
}
